import * as c from '@iamo/custody-lib';
import { IConnection } from './Connection';

export interface IChallenge {
    challenger: string;
    nextOwner: string;
    interval: string;
    startOfChallenge: string;
}

/**
 * Challenge-class of the wallet
 */
export class Challenge {
    
    // The blockchain property of the backend connection object.
    connection: IConnection

    /**
     * initializes the class
     * @param connection The backend connection object.
     */
    constructor(connection: IConnection){
        this.connection = connection
    }

    /**
     * Creates a new challenge on an owner. Can only be called by a `challenger`.
     * @param moduleInstanceAddress The CustodyModule instance address
     * @param ownerToChallenge The owner who will be challenged
     * @param successor The successor who will get the roles of the ownerToChallenge if the challenge succeeds
     * @returns The transaction-receipt
     */
    async challengeOwner(
        moduleInstanceAddress: string,
        ownerToChallenge: string, 
        successor: string
    ): Promise<any> {
        return c.Challenge.challengeOwner(this.connection, moduleInstanceAddress, ownerToChallenge, successor)
    }

    /**
     * Allows an owner to respond to a challenge on themselves. Once a challenge is responded to the challenge is removed.
     * If a challenge is responded to after the challenge interval is up the challenge will not be removed.
     * @param moduleInstanceAddress The Custody Module instance address
     * @returns The transaction-receipt
     */
    async respondToChallenge(
        moduleInstanceAddress: string
    ): Promise<any> {
        return c.Challenge.respondToChallenge(this.connection, moduleInstanceAddress)
    }

    /**
     * Allows a challenger to remove a challenge on an owner. Can only be called by a challenger.
     * @param moduleInstanceAddress The Custody Module instance address
     * @param owner The owner who has been challenged
     * @returns The transaction-receipt
     */
    async removeChallenge(
        moduleInstanceAddress: string,
        owner: string
    ): Promise<any> {
        return c.Challenge.removeChallenge(this.connection, moduleInstanceAddress, owner)
    }

    /**
     * Allows the roles of an owner to be transferred if the challenge interval
     * has expired. This function cna be called by any account.
     * @param moduleInstanceAddress The backend connection object
     * @param ownerToTransfer The Custody module instance address
     * @returns The owner whose challenge has finished the challenge interval has expired
     */
    async transferOwnership(
        moduleInstanceAddress: string,
        ownerToTransfer: string
    ): Promise<any> {
        return c.Challenge.transferOwnership(this.connection, moduleInstanceAddress, ownerToTransfer)
    }

    /**
     * Gets a challenge of an owner. If there is no active challenge an empty challenge object will be returned.
     * @param moduleInstanceAddress The IAMO Safe instance address
     * @param owner The owner to get the challenge of
     * @returns the challenge object
     */
    async getChallenge(
        moduleInstanceAddress: string,
        owner: string
    ): Promise<IChallenge> {
        return c.Challenge.getChallenge(this.connection, moduleInstanceAddress, owner)
    }

    /**
     * Returns if the owner has an active challenge.
     * @param multisigInstanceAddress The IAMO Safe instance address
     * @param owner The owner to check
     * @returns true if the owner has an active challenge
     */
    async hasActiveChallenge(
        multisigInstanceAddress: string,
        owner: string
    ): Promise<boolean> {
        return c.Challenge.hasActiveChallenge(this.connection, multisigInstanceAddress, owner)
    }

    /**
     * Joins a challenge (Can only be done by challenger). This reduces a challenge interval by
     * 90% per each challenger that joins a challenge.
     * @param moduleInstanceAddress The Custody module instance address
     * @param owner The owner who is being challenged
     * @returns The transaction-receipt
     */
    async joinChallenge(
        moduleInstanceAddress: string,
        owner: string
    ): Promise<any> {
        return c.Challenge.joinChallenge(this.connection, moduleInstanceAddress, owner)
    }

    /**
     * Gets the interval of a challenger.
     * @param instanceAddress The IAMO Safe instance address
     * @param challenger The challenger to get the interval of
     * @returns the challengers interval
     */
    async getChallengerInterval(
        instanceAddress: string, 
        challenger: string
    ): Promise<string> {
        return c.Challenge.getChallengerInterval(this.connection, instanceAddress, challenger)
    }

}

export default Challenge